import React, { useEffect, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { colors } from '../../../styles/fontStyles';
import ComparisonSelector from '../../../reports/ComparisonSelector';
import { CompoundMetaData, DemographicData } from '../../../types/reports';
import { RequestOptions } from '../../../types/httpRequests';
import useBackendApi from '../../../hooks/useBackendApi';
import useSnackBarAlertMessages from '../../../hooks/useSnackBarAlertMessages';
import GradientCircularProgress from '../../common/GradientCircularProgress';
import { datasetFilterConfig, DatasetKey } from '../../../configs/datasetConfig';
import NasemScoreTimelineChart from './NasemScoreTimelineChart';
import { getSelectedData, isCompoundInNHANES } from '../../../utils/reports';
import CompoundInNHANESTimelineChart from './CompoundInNHANESTimelineChart';
import CompoundNotInNHANESTimelineChart from './CompoundNotInNHANESTimelineChart';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

type TimelineChartContainerProps = {
  allResults: any[];
  compoundMetaData: Record<string, CompoundMetaData>;
};

const backendURL = process.env.REACT_APP_BACKEND_URL;
const MAX_RESULTS_TO_SHOW = 5;

const TimelineChartContainer: React.FC<TimelineChartContainerProps> = ({ allResults, compoundMetaData }) => {
  allResults = allResults.slice(-MAX_RESULTS_TO_SHOW);

  const [selectedDataset, setSelectedDataset] = useState<DatasetKey>('NHANES');
  const [selectedFilter, setSelectedFilter] = useState<string>(datasetFilterConfig['NHANES'].defaultFilter);
  const [selectedCompound, setSelectedCompound] = useState<string>('');
  const [reportDemographicData, setReportDemographicData] = useState<DemographicData | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();

  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { apiFetchRequest } = useBackendApi();

  const handleDatasetChange = (dataset: DatasetKey, filter: string) => {
    setSelectedDataset(dataset);
    setSelectedFilter(filter);
  };

  useEffect(() => {
    const handlefetchNhanesDemographicData = async () => {
      const endpointUrl: string = `${backendURL}/nhanes/demographic-data`;
      const apiRequestOptions: RequestOptions = {
        endpoint: endpointUrl,
      };

      try {
        const demographicDataResponse = await apiFetchRequest(apiRequestOptions);
        if (demographicDataResponse.status !== 200) {
          addAlertMessages({ message: 'Error fetching demographic data', severityType: 'error' });
          return;
        }
        setReportDemographicData(demographicDataResponse.body);
      } catch (error) {
        console.log('error fetching demographic data', error);
        addAlertMessages({ message: 'Error fetching demographic data', severityType: 'error' });
      } finally {
        setLoading(false);
      }
    };
    handlefetchNhanesDemographicData();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <GradientCircularProgress loadingMessage="Loading Graph..." />
      </Box>
    );
  }

  const renderTimelineGraphs = (
    reportDemographicData: DemographicData | undefined,
    selectedDataset: string,
    selectedFilter: string,
    allResults: any[],
    selectedCompound: string,
    compoundMetaData: Record<string, CompoundMetaData>,
  ) => {
    if (!reportDemographicData || isMobile) return <></>;

    const graphData = getSelectedData(reportDemographicData, selectedDataset, selectedFilter, selectedCompound);
    if (!graphData) {
      return <></>;
    }

    // If selectedCompound is empty, return the default chart. i.e. Nasem score timeline chart
    if (!selectedCompound) {
      return (
        <NasemScoreTimelineChart graphData={graphData} allResults={allResults} selectedCompound={selectedCompound} />
      );
    }

    // Determine which chart to render based on NHANES whether compound is present in NHANES or not.
    return isCompoundInNHANES(reportDemographicData, selectedFilter, selectedCompound) ? (
      <CompoundInNHANESTimelineChart
        graphData={graphData}
        allResults={allResults}
        selectedCompound={selectedCompound}
        compoundMetaData={compoundMetaData}
      />
    ) : (
      <CompoundNotInNHANESTimelineChart
        allResults={allResults}
        selectedCompound={selectedCompound}
        compoundMetaData={compoundMetaData}
      />
    );
  };

  const showDemographicFilters =
    selectedCompound && reportDemographicData
      ? isCompoundInNHANES(reportDemographicData, selectedFilter, selectedCompound)
      : true;

  const compoundFullName =
    selectedCompound && compoundMetaData?.[selectedCompound]?.fullname
      ? compoundMetaData[selectedCompound].fullname
      : selectedCompound || '';

  return (
    <>
      {renderSnackBarAlertComponent()}
      <Box
        sx={{
          marginTop: '12px',
          borderRadius: '16px',
          background: theme.custom.report.timeline.container.backgroundColor,
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Comparison Selector */}
        <Box sx={{ width: '100%', marginBottom: '16px' }}>
          <ComparisonSelector
            onDatasetChange={handleDatasetChange}
            showCompoundDropdownFilter
            compoundMetaData={compoundMetaData}
            setSelectedCompound={setSelectedCompound}
            selectedCompound={selectedCompound}
            showDemographicFilters={showDemographicFilters}
            compoundFullName={compoundFullName}
          />
        </Box>

        {/* Only for large screens, show the timeline chart */}
        <Box sx={{ width: '100%' }}>
          {renderTimelineGraphs(
            reportDemographicData,
            selectedDataset,
            selectedFilter,
            allResults,
            selectedCompound,
            compoundMetaData,
          )}
        </Box>
      </Box>
    </>
  );
};

export default TimelineChartContainer;
